import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import Logo from './Logo';
import MemberAvatar from '../member/MemberAvatar';
import { useAuth } from '../../auth/useAuth';
import { useJoinVisible } from '../../hooks/useJoinVisible';
import { APP_VERSION, CLUB } from '../../utils/constants';
import { MEMBERSHIP } from '../../utils/membership';
import { allowPublicHome } from '../../utils/publicHome';
import { maySeeCrewArea } from '../../utils/membership';

const NAV_ITEMS = [
  { to: '/', label: 'Start', end: true },
  { to: '/termine', label: 'Termine' },
  { to: '/galerie', label: 'Galerie' },
  // Nur für nicht Angemeldete - siehe useJoinVisible
  { to: '/mitglied-werden', label: 'Mitglied werden', joinOnly: true },
];

// Die Orte des Mitgliederbereichs, die allen offenstehen. Dieselbe Liste
// steht als Reiterleiste im MemberLayout - hier ist sie der Weg dorthin von
// JEDER Seite aus, auch von den öffentlichen und aus dem Admin-Bereich, der
// keine Reiterleiste hat.
const MEMBER_ITEMS = [
  { to: '/intern', label: 'Übersicht', end: true },
  { to: '/intern/termine', label: 'Termine' },
  { to: '/intern/aushang', label: 'Aushang' },
  { to: '/intern/crew', label: 'Mitglieder' },
  { to: '/intern/die-crew', label: 'Die Crew', nurCrew: true },
  { to: '/intern/vernetzen', label: 'Vernetzen' },
  { to: '/intern/karte', label: 'Karte' },
  { to: '/intern/galerie', label: 'Galerie' },
  { to: '/intern/merch', label: 'Merch' },
  { to: '/intern/wiki', label: 'Wiki' },
  { to: '/intern/ideen', label: 'Ideen' },
];

/**
 * Kopfleiste - zwei Anordnungen in einer Komponente.
 *
 * Gast: Logo links, Navigation rechts, Burger erst auf schmalen Bildschirmen.
 * Angemeldet: Burger LINKS auf jeder Breite, Logo mittig, Profilbild rechts.
 * Der Avatar führt direkt ins eigene Profil, alles Weitere liegt im Menü -
 * also auch das, was vorher im Benutzermenü mitten im Inhalt hing
 * (MemberMenu.js, seither nicht mehr montiert).
 *
 * Die Mitglieder-Anordnung greift erst, wenn der Anmeldestand feststeht.
 * `loading` dauert zwei Roundtrips (Token + erster Profil-Snapshot), und
 * während dieser Zeit wird zugunsten des Gastes entschieden - dieselbe
 * Haltung wie in useJoinVisible. Ein Spinner in der Kopfleiste wäre der
 * schlechtere Tausch: Er träfe jeden Besucher der Startseite.
 */
export default function Header() {
  const [open, setOpen] = useState(false);
  const { user, loading, profile, isActiveMember, isAdmin, isOrganizer, membership, logout } =
    useAuth();
  // Dieselbe Frage wie im Routen-Gate und in der Reiterleiste, aus
  // derselben Funktion (v0.28.0).
  const crewSichtbar = maySeeCrewArea(membership, isAdmin);
  const joinVisible = useJoinVisible();
  const location = useLocation();
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const toggleRef = useRef(null);

  const memberView = !loading && Boolean(user);
  const name = profile?.displayName || user?.email || '';
  // Die Änderungsliste ist für die, die den Club machen - Crew, Orga, Admin.
  const seesReleaseNotes =
    isAdmin || isOrganizer || (isActiveMember && membership === MEMBERSHIP.CREW);

  const navItems = NAV_ITEMS.filter((item) => joinVisible || !item.joinOnly);

  // Menü nach jedem Seitenwechsel schließen - sonst bleibt es auf dem
  // Handy über der neuen Seite stehen.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Klick daneben und Escape schließen das Menü.
  //
  // Ein Klickfeld als eigenes Element über der Seite (position: fixed;
  // inset: 0) geht hier NICHT: Der Header hat backdrop-filter und ist damit
  // Containing Block für fixierte Nachfahren - das Feld würde nur den
  // Header-Streifen abdecken. Deshalb ein Listener am Dokument.
  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      // Fokus zurück an den Auslöser - sonst landet die Tastaturbedienung
      // nach dem Schließen am Seitenanfang.
      toggleRef.current?.focus();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Abmelden von einer öffentlichen Seite navigiert nicht - ohne dieses
  // setOpen bliebe das Panel offen stehen und zeigte plötzlich das
  // Gast-Menü.
  function handleLogout() {
    setOpen(false);
    logout();
  }

  function handlePublicHome() {
    setOpen(false);
    allowPublicHome();
    navigate('/');
  }

  const toggle = (
    <button
      type="button"
      ref={toggleRef}
      className="mt-header__toggle"
      aria-expanded={open}
      aria-controls="hauptnavigation"
      onClick={() => setOpen((v) => !v)}
    >
      <span className="mt-sr-only">Menü {open ? 'schließen' : 'öffnen'}</span>
      <span aria-hidden="true">{open ? '✕' : '☰'}</span>
    </button>
  );

  if (memberView) {
    return (
      <header className="mt-header" ref={rootRef}>
        <a className="mt-skip" href="#inhalt">
          Zum Inhalt springen
        </a>
        <div className="mt-container mt-header__inner mt-header__inner--member">
          {toggle}

          {/* Das Logo führt für Mitglieder nach innen: „/" leitet sie ohnehin
              dorthin um, der Klick wäre sonst ein Rückprall. */}
          <Link
            to="/intern"
            className="mt-header__brand"
            aria-label={`${CLUB.name} – Mein Bereich`}
          >
            <Logo className="mt-logo--sm" />
          </Link>

          {/* Eigenes aria-label: Ohne hochgeladenes Bild rendert MemberAvatar
              die Initialen mit aria-hidden - der Link hätte sonst keinen
              zugänglichen Namen. */}
          <div className="mt-header__right">
            {/* Nur für Admins: Welche Version läuft gerade? Ohne CI die
                einfachste Antwort auf „ist der Deploy durch?". Führt zu den
                Neuerungen, weil man danach meist wissen will, was drin ist. */}
            {isAdmin && (
              <Link
                to="/intern/neuerungen"
                className="mt-header__version"
                title="Ausgelieferte Version"
              >
                {APP_VERSION}
              </Link>
            )}
            <Link
              to="/intern/profil"
              className="mt-header__avatar"
              aria-label={name ? `Mein Profil, ${name}` : 'Mein Profil'}
            >
              <MemberAvatar member={profile} size="sm" />
            </Link>
          </div>

          <nav
            id="hauptnavigation"
            className={`mt-header__nav mt-header__nav--account ${open ? 'is-open' : ''}`}
            aria-label="Mitgliedernavigation"
          >
            <div className="mt-header__who">
              <strong>{name || 'Mein Profil'}</strong>
              {user?.email && name !== user.email && (
                <span className="mt-muted">{user.email}</span>
              )}
              {/* Auf schmalen Handys fehlt die Nummer im Header (kein Platz
                  neben dem Logo) - hier steht sie auf jeder Breite. */}
              {isAdmin && <span className="mt-muted">Version {APP_VERSION}</span>}
            </div>

            {/* Wer noch auf die Freigabe wartet, sieht nur den Weg zur
                Statusseite - die Orte dahinter sind für ihn ohnehin zu. */}
            {isActiveMember &&
              MEMBER_ITEMS.filter((item) => !item.nurCrew || crewSichtbar).map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => `mt-header__link ${isActive ? 'is-active' : ''}`}
                >
                  {item.label}
                </NavLink>
              ))}
            {!isActiveMember && (
              <NavLink to="/intern" end className="mt-header__link">
                Mein Bereich
              </NavLink>
            )}

            <span className="mt-header__sep" aria-hidden="true" />

            <NavLink
              to="/intern/profil"
              className={({ isActive }) => `mt-header__link ${isActive ? 'is-active' : ''}`}
            >
              Mein Profil
            </NavLink>
            {isOrganizer && (
              <NavLink
                to="/intern/organisieren"
                className={({ isActive }) => `mt-header__link ${isActive ? 'is-active' : ''}`}
              >
                Organisieren
              </NavLink>
            )}
            {/* An derselben Stelle wie „Organisieren": Wer noch keiner ist,
                findet hier den Weg dorthin. Bis v0.19.1 stand die Bewerbung
                nur ganz unten im Profil. */}
            {isActiveMember && !isOrganizer && (
              <NavLink
                to="/intern/orga-werden"
                className={({ isActive }) => `mt-header__link ${isActive ? 'is-active' : ''}`}
              >
                Orga werden
              </NavLink>
            )}
            {seesReleaseNotes && (
              <NavLink
                to="/intern/neuerungen"
                className={({ isActive }) => `mt-header__link ${isActive ? 'is-active' : ''}`}
              >
                Neuerungen
              </NavLink>
            )}
            {isAdmin && (
              <NavLink
                to="/admin"
                className={({ isActive }) => `mt-header__link ${isActive ? 'is-active' : ''}`}
                end
              >
                Admin-Bereich
              </NavLink>
            )}
            {/* Eigener Eintrag, weil der Ideen-Manager eine eigene Seite ist
                und nicht als Reiter im Admin-Bereich sitzt. `end` am Eintrag
                darüber verhindert, dass „Admin-Bereich" hier mit als aktiv
                markiert wird - /admin ist ein Präfix von /admin/ideen.

                „Ideen-Manager" und nicht „Ideen": Weiter oben steht bereits
                ein Eintrag „Ideen" (der Vorschlagskasten für Mitglieder,
                /intern/ideen). Zwei gleich benannte Zeilen in DEMSELBEN Menü
                sind für den Leser nicht auflösbar - er kann nur raten, welche
                wohin führt. Die beiden Seiten machen auch Verschiedenes:
                hier wird entwickelt, dort vorgeschlagen. */}
            {isAdmin && (
              <NavLink
                to="/admin/ideen"
                className={({ isActive }) => `mt-header__link ${isActive ? 'is-active' : ''}`}
              >
                Ideen-Manager
              </NavLink>
            )}
            {isAdmin && (
              <NavLink
                to="/admin/log"
                className={({ isActive }) => `mt-header__link ${isActive ? 'is-active' : ''}`}
              >
                Protokoll
              </NavLink>
            )}

            <span className="mt-header__sep" aria-hidden="true" />

            {/* Die öffentliche Startseite steht für Mitglieder nicht mehr in
                der Kopfleiste - ohne diesen Eintrag wäre sie für sie gar nicht
                mehr erreichbar.

                Hier stand bis v0.18.1 ein zweiter Eintrag „Galerie" auf
                /galerie, mit derselben Begründung. Die galt mit v0.15.0 nicht
                mehr: Seither hängt die Galerie unter /intern/galerie und steht
                oben in der Liste. Übrig blieb eine zweite Zeile mit
                identischem Wort, die den Leser aus dem Mitgliederbereich
                geworfen hätte - und wer die öffentliche Fassung sehen will,
                nimmt den Eintrag direkt darunter. */}
            <button type="button" className="mt-header__link" onClick={handlePublicHome}>
              Öffentliche Seite
            </button>
            <a
              className="mt-header__ig"
              href={CLUB.instagram}
              target="_blank"
              rel="noopener noreferrer"
            >
              {CLUB.instagramHandle}
            </a>

            <button type="button" className="mt-header__link" onClick={handleLogout}>
              Abmelden
            </button>
          </nav>
        </div>
      </header>
    );
  }

  return (
    <header className="mt-header" ref={rootRef}>
      <a className="mt-skip" href="#inhalt">
        Zum Inhalt springen
      </a>
      <div className="mt-container mt-header__inner">
        <Link to="/" className="mt-header__brand" aria-label={`${CLUB.name} Startseite`}>
          <Logo className="mt-logo--sm" />
        </Link>

        {toggle}

        <nav
          id="hauptnavigation"
          className={`mt-header__nav ${open ? 'is-open' : ''}`}
          aria-label="Hauptnavigation"
        >
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `mt-header__link ${isActive ? 'is-active' : ''}`}
            >
              {item.label}
            </NavLink>
          ))}

          <a
            className="mt-header__ig"
            href={CLUB.instagram}
            target="_blank"
            rel="noopener noreferrer"
          >
            {CLUB.instagramHandle}
          </a>

          <Link to="/login" className="mt-btn mt-btn--primary mt-btn--sm">
            Anmelden
          </Link>
        </nav>
      </div>
    </header>
  );
}
